import React, { useMemo, useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import Select from 'react-select';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import GridViewIcon from '@mui/icons-material/GridView';
import Modal from '../components/Modal';
import OnboardStudents from '../components/OnboardStudents';
import TableWrapper from '../utils/TableWrapper';
import Spinner from '../components/spinner/Spinner';
import MainContent from '../components/MainContent';
import useGetUserRole from '../hooks/useGetUserRole';
import usePermissions from '../hooks/usePermissions';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import RegenerateDID from '../components/RegenerateDID';
import MoveCohortDeferees from '../components/MoveCohortDeferees';
import MoveCohortDropOffs from '../components/MoveCohortDropOffs';
import IssueClaims from '../components/IssueClaims';
import EditSponsorshipCohort from '../components/EditSponsorshipCohort';
import CohortCertSettings from '../components/CohortCertSettings';
import SpecializationTrackConfig from '../components/SpecializationTrackConfig';
import CertificateBatchDashboard from '../components/CertificateBatchDashboard';
import ChangeRequestDashboard from '../components/ChangeRequestDashboard';
import CohortOnboardingConfig from '../components/CohortOnboardingConfig';
import DeleteCohortWithStudents from '../components/DeleteCohortWithStudents';
import AssignCohortPrefix from '../components/AssignCohortPrefix';
import NotifyCohortStudentID from '../components/NotifyCohortStudentID';
import { canViewStudentIntelligence } from '../utils/permissionKeys';
import {
  hasFullStudentDetailAccess,
  maskEmail,
  maskPhone,
  maskStudentNumber,
} from '../utils/studentPrivacy';
import { isAdminOrAbove, isSuperAdmin } from '../utils/roles';

function CohortDetail() {
  const navigate = useNavigate();

  const [editCohortModalOpen, setEditCohortModalOpen] = useState(false);
  const [onboardStudentsModalOpen, setOnboardStudentsModalOpen] =
    useState(false);
  const [certSettingsModalOpen, setCertSettingsModalOpen] = useState(false);
  const [regenerateDidModalOpen, setRegenerateDidModalOpen] = useState(false);
  const [issueClaimsModalOpen, setIssueClaimsModalOpen] = useState(false);
  const [moveCohortDefereesModalOpen, setMoveCohortDefereesModalOpen] =
    useState(false);
  const [moveCohortDropOffsModalOpen, setMoveCohortDropOffsModalOpen] =
    useState(false);
  const [onboardingConfigModalOpen, setOnboardingConfigModalOpen] =
    useState(false);
  const [selectedMoreActionOption, setSelectedMoreActionOption] =
    useState(null);
  const [deleteCohortModalOpen, setDeleteCohortModalOpen] = useState(false);
  const [assignPrefixModalOpen, setAssignPrefixModalOpen] = useState(false);
  const [notifyIdsModalOpen, setNotifyIdsModalOpen] = useState(false);
  const [specTracksModalOpen, setSpecTracksModalOpen] = useState(false);
  const [certBatchModalOpen, setCertBatchModalOpen] = useState(false);
  const [changeRequestsModalOpen, setChangeRequestsModalOpen] = useState(false);

  const role = useGetUserRole();

  const permissions = usePermissions();

  const { cohortName, cohortId, cohortStatus } = useParams();

  const toggleEditCohortModal = () => {
    setEditCohortModalOpen(!editCohortModalOpen);
  };

  const toggleOnboardStudentsModal = () => {
    setOnboardStudentsModalOpen(!onboardStudentsModalOpen);
  };

  const toggleCertSettingsModal = () => {
    setCertSettingsModalOpen(!certSettingsModalOpen);
  };

  const toggleRegenerateDidModal = () => {
    setRegenerateDidModalOpen(!regenerateDidModalOpen);
  };

  const toggleIssueClaimsModal = () => {
    setIssueClaimsModalOpen(!issueClaimsModalOpen);
  };

  const toggleMoveCohortDefereesModal = () => {
    setMoveCohortDefereesModalOpen(!moveCohortDefereesModalOpen);
  };

  const toggleMoveCohortDropOffsModal = () => {
    setMoveCohortDropOffsModalOpen(!moveCohortDropOffsModalOpen);
  };

  const toggleOnboardingConfigModal = () => {
    setOnboardingConfigModalOpen(!onboardingConfigModalOpen);
  };

  const toggleDeleteCohortModal = () => {
    setDeleteCohortModalOpen(!deleteCohortModalOpen);
  };

  const toggleAssignPrefixModal = () => {
    setAssignPrefixModalOpen(!assignPrefixModalOpen);
  };

  const toggleNotifyIdsModal = () => {
    setNotifyIdsModalOpen(!notifyIdsModalOpen);
  };

  const toggleSpecTracksModal = () => {
    setSpecTracksModalOpen(!specTracksModalOpen);
  };

  const toggleCertBatchModal = () => {
    setCertBatchModalOpen(!certBatchModalOpen);
  };

  const toggleChangeRequestsModal = () => {
    setChangeRequestsModalOpen(!changeRequestsModalOpen);
  };

  const { data: cohortData } = useFetchDatav2(
    ['cohort', cohortId],
    `/cohorts/${cohortId}`,
    {},
    null,
    true
  );

  const moreActionsOptions = [
    { value: 'Regenerate_Student_DIDs', label: 'Regenerate Student DIDs' },
    { value: 'Issue_Student_Claims', label: 'Issue Students Claims' },
    { value: 'Move_Cohort_Deferees', label: 'Move Cohort Deferees' },
    { value: 'Move_Cohort_DropOffs', label: 'Move Cohort DropOffs' },
    ...(isAdminOrAbove(role)
      ? [{ value: 'Assign_Prefix', label: 'Assign Student ID Prefix' }]
      : []),
    ...(isAdminOrAbove(role)
      ? [{ value: 'Onboarding_Config', label: 'Onboarding Config' }]
      : []),
    ...(isAdminOrAbove(role) && cohortData?.data?.cohort?.prefix_id
      ? [{ value: 'Notify_Student_IDs', label: 'Notify Students of IDs' }]
      : []),
    ...(isAdminOrAbove(role) && cohortData?.data?.cohort?.has_onboarding_config
      ? [{ value: 'Trigger_Onboarding', label: 'Trigger Onboarding' }]
      : []),
    ...(isSuperAdmin(role)
      ? [
          {
            value: 'Delete_Cohort',
            label: 'Delete Cohort & Students',
          },
        ]
      : []),
  ];

  const handleMoreActionsdropdown = (selectedOption) => {
    switch (selectedOption.value) {
      case 'Regenerate_Student_DIDs':
        toggleRegenerateDidModal();
        break;
      case 'Issue_Student_Claims':
        toggleIssueClaimsModal();
        break;
      case 'Move_Cohort_Deferees':
        toggleMoveCohortDefereesModal();
        break;
      case 'Move_Cohort_DropOffs':
        toggleMoveCohortDropOffsModal();
        break;
      case 'Onboarding_Config':
        toggleOnboardingConfigModal();
        break;
      case 'Trigger_Onboarding':
        toggleOnboardingConfigModal();
        break;
      case 'Assign_Prefix':
        toggleAssignPrefixModal();
        break;
      case 'Delete_Cohort':
        toggleDeleteCohortModal();
        break;
      case 'Notify_Student_IDs':
        toggleNotifyIdsModal();
        break;
      default:
        break;
    }
    setSelectedMoreActionOption(null);
  };

  const columns = useMemo(() => {
    const hidePii = !hasFullStudentDetailAccess(role);
    const canIntel = canViewStudentIntelligence(permissions, role);
    const formatLastLogin = (lastLogin) => {
      if (!lastLogin) return 'No Record';
      return new Date(lastLogin).toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };
    return [
      {
        title: 'First Name ',
        field: 'firstname',
        render: (row) => <p className="capitalize">{row.firstname}</p>,
      },
      {
        title: 'Last Name ',
        field: 'lastname',
        render: (row) => <p className="capitalize">{row.lastname}</p>,
      },
      {
        title: 'Student ID',
        field: 'student_number',
        render: (row) => (
          <p className="font-mono text-xs">
            {hidePii
              ? maskStudentNumber(row.student_number || '')
              : row.student_number || <span className="text-gray-400">-</span>}
          </p>
        ),
      },
      {
        title: 'Email',
        field: 'email',
        render: (row) => (
          <p className="lowercase">
            {hidePii ? maskEmail(row.email || '') : row.email}
          </p>
        ),
      },
      {
        title: 'Phone Number',
        field: 'phone_number',
        render: (row) => (
          <p>
            {hidePii ? maskPhone(row.phone_number || '') : row.phone_number}
          </p>
        ),
      },
      {
        title: 'Country',
        field: 'country',
        render: (row) => (
          <p>
            {hidePii && row.country
              ? `${row.country.slice(0, 2)}••`
              : row.country}
          </p>
        ),
      },
      {
        title: 'Gender',
        field: 'gender',
        render: (row) => (
          <p className="max-w-xs line-clamp-2 capitalize">
            {hidePii ? '•••' : row.gender || 'N/A'}
          </p>
        ),
      },
      {
        title: 'Academic Level',
        field: 'level_of_education',
        render: (row) => <p>{hidePii ? '••••' : row.level_of_education}</p>,
      },
      {
        title: 'Last Login',
        field: 'last_login',
        render: (row) => (
          <p className="">
            {hidePii ? 'Restricted' : formatLastLogin(row.last_login)}
          </p>
        ),
      },
      {
        title: 'Actions',
        render: (row) =>
          canIntel ? (
            <button
              className={`bg-persian-500 px-4 py-1 rounded-md text-white text-sm cursor-pointer hover:bg-persian-700 space-x-2 flex items-center `}
              type="button"
              onClick={() =>
                navigate(
                  `/student-intelligence?id=${encodeURIComponent(row.id)}`
                )
              }
            >
              <p>More Details</p>
            </button>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          ),
      },
    ];
  }, [role, permissions, navigate]);

  const { data, isLoading } = useFetchDatav2(
    ['students', cohortId],
    '/users/student',
    {
      params: { cohort_id: cohortId },
    },
    "Couldn't get details. Please try again",
    true
  );

  const renderActions = () => {
    const isEnded = cohortStatus === 'ended';
    const isAdmin = isAdminOrAbove(role);
    const isSAdmin = isSuperAdmin(role);
    const canUpdate = permissions?.includes('UpdateCohort') || isSAdmin;
    const canOnboard = permissions?.includes('OnboardStudents') || isSAdmin;

    return (
      <div className="flex items-center space-x-4">
        {canUpdate && (
          <button
            type="button"
            className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
            onClick={toggleEditCohortModal}
          >
            Edit Cohort
          </button>
        )}

        {isEnded && isAdmin && (
          <>
            <button
              type="button"
              className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
              onClick={toggleCertSettingsModal}
            >
              Cert Settings
            </button>
            <button
              type="button"
              className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
              onClick={toggleSpecTracksModal}
            >
              Specialization Tracks
            </button>
            <button
              type="button"
              className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
              onClick={toggleCertBatchModal}
            >
              Certificate Management
            </button>
            <button
              type="button"
              className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
              onClick={toggleChangeRequestsModal}
            >
              Change Requests
            </button>
          </>
        )}

        {!isEnded && canOnboard && (
          <button
            type="button"
            className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
            onClick={toggleOnboardStudentsModal}
          >
            Add Students
          </button>
        )}

        {isAdmin && (
          <div className="flex items-center">
            <Select
              value={selectedMoreActionOption}
              className="react-dropdown"
              classNamePrefix="dropdown"
              options={moreActionsOptions}
              isClearable
              onChange={(selectedOption) => {
                setSelectedMoreActionOption(selectedOption);
                handleMoreActionsdropdown(selectedOption);
              }}
              placeholder="More Actions"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <MainContent full>
      <div className="bg-white px-2 py-5 rounded-lg flex justify-between items-center">
        <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon />}>
          <NavLink
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
            color="inherit"
            to="/"
            className="flex items-center"
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <p>Home</p>
          </NavLink>
          <NavLink
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
            color="inherit"
            to="/cohorts"
            className="flex items-center"
          >
            <GridViewIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <p>Cohorts</p>
          </NavLink>
        </Breadcrumbs>
        <div className="space-x-4 flex items-center">{renderActions()}</div>
      </div>
      <Modal
        modalOpen={editCohortModalOpen}
        onClose={toggleEditCohortModal}
        title="Update Sponsorship Cohort"
      >
        <EditSponsorshipCohort
          cohortId={cohortId}
          toggleModal={toggleEditCohortModal}
        />
      </Modal>
      <Modal
        modalOpen={certSettingsModalOpen}
        onClose={toggleCertSettingsModal}
        title="Cohort Certificate Settings"
      >
        <CohortCertSettings
          cohortId={cohortId}
          toggleModal={toggleCertSettingsModal}
        />
      </Modal>
      <Modal
        modalOpen={onboardStudentsModalOpen}
        onClose={toggleOnboardStudentsModal}
        title={`Onboard Learners to ${cohortName}`}
      >
        <OnboardStudents toggleModal={toggleOnboardStudentsModal} />
      </Modal>
      <Modal
        modalOpen={regenerateDidModalOpen}
        onClose={toggleRegenerateDidModal}
        title="Regenerate Missing Student Decentralized IDs"
      >
        <RegenerateDID
          cohortId={cohortId}
          cohortName={cohortName}
          toggleModal={toggleRegenerateDidModal}
        />
      </Modal>
      <Modal
        modalOpen={issueClaimsModalOpen}
        onClose={toggleIssueClaimsModal}
        title="Issue Claims to Cohort Students"
      >
        <IssueClaims
          cohortId={cohortId}
          cohortName={cohortName}
          toggleModal={toggleIssueClaimsModal}
        />
      </Modal>
      <Modal
        modalOpen={moveCohortDefereesModalOpen}
        onClose={toggleMoveCohortDefereesModal}
        title={`Move ${cohortName} Deferees`}
      >
        <MoveCohortDeferees
          cohortId={cohortId}
          cohortName={cohortName}
          toggleModal={toggleMoveCohortDefereesModal}
        />
      </Modal>
      <Modal
        modalOpen={moveCohortDropOffsModalOpen}
        onClose={toggleMoveCohortDropOffsModal}
        title={`Move ${cohortName} Drop-offs`}
      >
        <MoveCohortDropOffs
          cohortId={cohortId}
          cohortName={cohortName}
          toggleModal={toggleMoveCohortDropOffsModal}
        />
      </Modal>
      <Modal
        modalOpen={onboardingConfigModalOpen}
        onClose={toggleOnboardingConfigModal}
        title={`Onboarding Configuration - ${cohortName}`}
      >
        <CohortOnboardingConfig
          cohortId={cohortId}
          toggleModal={toggleOnboardingConfigModal}
        />
      </Modal>
      {deleteCohortModalOpen && (
        <DeleteCohortWithStudents
          cohortId={cohortId}
          cohortName={cohortName}
          onClose={toggleDeleteCohortModal}
        />
      )}
      <Modal
        modalOpen={assignPrefixModalOpen}
        onClose={toggleAssignPrefixModal}
        title={`Assign Student ID Prefix - ${cohortName}`}
      >
        <AssignCohortPrefix
          cohortId={cohortId}
          cohortName={cohortName}
          currentPrefixId={cohortData?.data?.cohort?.prefix_id}
          toggleModal={toggleAssignPrefixModal}
        />
      </Modal>
      <Modal
        modalOpen={notifyIdsModalOpen}
        onClose={toggleNotifyIdsModal}
        title={`Notify Students of IDs - ${cohortName}`}
      >
        <NotifyCohortStudentID
          cohortId={cohortId}
          cohortName={cohortName}
          toggleModal={toggleNotifyIdsModal}
        />
      </Modal>
      <Modal
        modalOpen={specTracksModalOpen}
        onClose={toggleSpecTracksModal}
        title={`Specialization Tracks - ${cohortName}`}
      >
        <SpecializationTrackConfig cohortId={cohortId} />
      </Modal>
      <Modal
        modalOpen={certBatchModalOpen}
        onClose={toggleCertBatchModal}
        title={`Certificate Management - ${cohortName}`}
        wide
      >
        <CertificateBatchDashboard cohortId={cohortId} />
      </Modal>
      <Modal
        modalOpen={changeRequestsModalOpen}
        onClose={toggleChangeRequestsModal}
        title={`Change Requests - ${cohortName}`}
      >
        <ChangeRequestDashboard cohortId={cohortId} />
      </Modal>
      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center bg-white">
            <Spinner />
          </div>
        ) : (
          <TableWrapper
            title={`List of Students in Sponsored Cohort - ${cohortName}`}
            columns={columns}
            data={data ? data?.data?.students : []}
            options={{
              rowStyle: {
                overflowWrap: 'break-word',
              },
            }}
          />
        )}
      </div>
    </MainContent>
  );
}

export default CohortDetail;
